import React, { useEffect, useRef, useState } from "react";
import { AreaChart, Area, BarChart, Bar, ResponsiveContainer, YAxis } from "recharts";

const RED = "#E11D2B";

const rnd = (min, max) => min + Math.random() * (max - min);

const WidgetShell = ({ title, children, testid }) => (
  <div data-testid={testid} className="hud-panel-silver rounded-lg p-3 scanlines overflow-hidden">
    <div className="flex items-center justify-between mb-2">
      <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-400">{title}</span>
      <span className="h-1.5 w-1.5 rounded-full bg-brand-red blink-dot" />
    </div>
    {children}
  </div>
);

const Gauge = ({ value, label }) => {
  const angle = -110 + (value / 100) * 220;
  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 100 62" className="w-full max-w-[130px]">
        <path d="M 12 54 A 42 42 0 1 1 88 54" fill="none" stroke="#242a35" strokeWidth="7" strokeLinecap="round" />
        <path d="M 12 54 A 42 42 0 1 1 88 54" fill="none" stroke={RED} strokeWidth="7" strokeLinecap="round"
          strokeDasharray={`${(value / 100) * 193} 193`} style={{ filter: "drop-shadow(0 0 4px rgba(225,29,43,0.7))", transition: "stroke-dasharray 0.8s ease" }} />
        <g transform={`rotate(${angle} 50 52)`} style={{ transition: "transform 0.8s ease" }}>
          <line x1="50" y1="52" x2="50" y2="18" stroke="#fff" strokeWidth="1.6" />
        </g>
        <circle cx="50" cy="52" r="3.4" fill={RED} />
        <text x="50" y="44" textAnchor="middle" fill="#fff" fontSize="11" fontFamily="JetBrains Mono" fontWeight="700">{Math.round(value)}</text>
      </svg>
      <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-500 -mt-1">{label}</span>
    </div>
  );
};

const BAYS = [
  { bay: "Bay 01", job: "V8 engine tune", state: "ACTIVE" },
  { bay: "Bay 02", job: "Suspension calibration", state: "SCANNING" },
  { bay: "Bay 03", job: "Repco warranty service", state: "QUEUED" },
];

export const CommandCenter = () => {
  const [, setTick] = useState(0);
  const wave = useRef(Array.from({ length: 28 }, (_, i) => ({ i, v: 40 + Math.sin(i / 2.5) * 22 + rnd(-6, 6) })));
  const bars = useRef(Array.from({ length: 16 }, (_, i) => ({ i, v: rnd(18, 95) })));
  const gauges = useRef({ torque: 64, boost: 71 });

  useEffect(() => {
    const id = setInterval(() => {
      const w = wave.current.slice(1);
      const last = w[w.length - 1];
      w.push({ i: last.i + 1, v: Math.max(8, Math.min(95, last.v + rnd(-14, 14))) });
      wave.current = w;
      bars.current = bars.current.map((b) => ({ ...b, v: Math.max(12, Math.min(98, b.v + rnd(-18, 18))) }));
      gauges.current = {
        torque: Math.max(35, Math.min(95, gauges.current.torque + rnd(-9, 9))),
        boost: Math.max(30, Math.min(92, gauges.current.boost + rnd(-7, 7))),
      };
      setTick((t) => t + 1);
    }, 1100);
    return () => clearInterval(id);
  }, []);

  return (
    <aside data-testid="command-center" className="hud-panel rounded-xl p-4 flex flex-col gap-3 h-full red-sweep">
      <div className="flex items-center gap-2">
        <span className="h-4 w-1 bg-brand-red" />
        <h2 className="font-tech uppercase text-lg tracking-wide text-white">Workshop Command Center</h2>
      </div>
      <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-brand-red -mt-2 mb-1">Live streaming telemetry</div>

      <WidgetShell title="Live data graph — RPM wave" testid="cc-wave">
        <div className="h-[84px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={wave.current} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="redFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={RED} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={RED} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <YAxis hide domain={[0, 100]} />
              <Area type="monotone" dataKey="v" stroke={RED} strokeWidth={1.6} fill="url(#redFill)" isAnimationActive={false} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </WidgetShell>

      <WidgetShell title="Diagnostic stream" testid="cc-bars">
        <div className="h-[72px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={bars.current} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
              <YAxis hide domain={[0, 100]} />
              <Bar dataKey="v" fill={RED} fillOpacity={0.75} isAnimationActive={false} radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </WidgetShell>

      <WidgetShell title="Performance meters" testid="cc-gauges">
        <div className="grid grid-cols-2 gap-2">
          <Gauge value={gauges.current.torque} label="Torque idx" />
          <Gauge value={gauges.current.boost} label="Boost psi" />
        </div>
      </WidgetShell>

      <WidgetShell title="Bay status monitor" testid="cc-bays">
        <div className="space-y-1.5">
          {BAYS.map((b) => (
            <div key={b.bay} className="flex items-center justify-between border border-zinc-800/80 bg-black/40 px-2.5 py-1.5 rounded">
              <div className="flex items-center gap-2">
                <span className={`h-1.5 w-1.5 rounded-full ${b.state === "ACTIVE" ? "bg-brand-red blink-dot" : b.state === "SCANNING" ? "bg-amber-400 blink-dot" : "bg-zinc-600"}`} />
                <span className="font-mono text-[10px] uppercase text-zinc-300">{b.bay}</span>
              </div>
              <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">{b.job}</span>
            </div>
          ))}
        </div>
      </WidgetShell>

      <WidgetShell title="System metrics" testid="cc-metrics">
        <div className="space-y-2">
          {[
            { k: "Oil viscosity index", v: `${(4.2 + (gauges.current.torque % 10) / 10).toFixed(1)} cSt`, pct: 78 },
            { k: "ECU codes cleared", v: "1,204 total", pct: 92 },
            { k: "Coolant delta-T", v: `${(6 + (gauges.current.boost % 8) / 2).toFixed(1)}°C`, pct: 64 },
          ].map((m) => (
            <div key={m.k}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-400">{m.k}</span>
                <span className="font-mono text-[10px] text-white">{m.v}</span>
              </div>
              <div className="mt-1 h-1 bg-zinc-800 rounded overflow-hidden">
                <div className="h-full bg-brand-red transition-all duration-1000" style={{ width: `${Math.min(98, m.pct + ((gauges.current.torque + gauges.current.boost) % 9))}%`, boxShadow: "0 0 6px rgba(225,29,43,0.8)" }} />
              </div>
            </div>
          ))}
        </div>
      </WidgetShell>
    </aside>
  );
};
